/**
 * UpcomingObligationsPageController — page dédiée `/echeances` (EPIC-030 / 30.5 — §15.D).
 *
 * Liste paginée (25/page, tri `dueDate` ASC fixe) du calendrier
 * d'obligations sur 12 mois glissants. Filtres combinables persistés
 * en querystring : `domain` (multi-sélection CSV), `period` (30/90/180/365 j,
 * défaut 90), `status` (`upcoming` / `overdue` / vide=toutes), `page`.
 * Synchronisation bidirectionnelle URL ↔ état (popstate restaure les
 * filtres précédents).
 *
 * Verbatim maquette `echeances.html` (sélecteurs `nm-filters`,
 * `nm-table`, `nm-pagination` du DS partagé). Mapping `domain` →
 * classe pill factorisé dans `utils/obligation_domain_mapping.js`
 * (partagé avec 30.4). Le bouton « Marquer comme fait » (POST) est
 * réservé aux rappels personnels (`domain === 'personal'`, endpoint
 * `mark-done` réel) ; toute obligation système rend un lien
 * « Préparer → » vers sa page spécialisée (défaut spec §15.D — cf. 99.62).
 *
 * États visibles : `loading` au connect / changement de filtre, `list`
 * (≥1 item), `empty` (0 item avec filtres actifs), `emptyAbsolute`
 * (0 item ET filtres = défaut). 401 reste géré globalement par `api.js`.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";
import { getDomainPill } from "/js/utils/obligation_domain_mapping.js";
import { formatAbsoluteDateWithWeekday } from "/js/utils/date_absolute.js";
import { formatRelativeDate } from "/js/utils/format.js";
import { cloneTemplate } from "/js/utils/template.js";
import { isChargePayable, mountObligationPaymentForm } from "/js/utils/obligation_payment_form.js";

const PERSONAL_DOMAIN = "personal";

const DEFAULT_PERIOD = 90;

export default class UpcomingObligationsPageController extends BaseController {
    static targets = [
        "domainCheckbox",
        "periodBtn",
        "statusBtn",
        "loading",
        "results",
        "list",
        "empty",
        "emptyAbsolute",
        "pagination",
        "rowTemplate",
        "pageButtonTemplate",
        "undatedSection",
        "undatedList",
        "undatedRowTemplate",
    ];
    static values = {
        perPage: { type: Number, default: 25 },
    };

    connect() {
        this.popstateHandler = () => {
            this.restoreFiltersFromUrl();
            this.reflectFiltersInDom();
            this.loadObligations();
        };
        window.addEventListener("popstate", this.popstateHandler);

        this.restoreFiltersFromUrl();
        this.reflectFiltersInDom();
        this.loadObligations();
    }

    disconnect() {
        if (this.popstateHandler) {
            window.removeEventListener("popstate", this.popstateHandler);
        }
    }

    // -------------------------------------------------------------------------
    // État filtres ↔ URL
    // -------------------------------------------------------------------------

    restoreFiltersFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const domainCsv = params.get("domain") ?? "";
        const periodRaw = parseInt(params.get("period") ?? "", 10);
        const period = [30, 90, 180, 365].includes(periodRaw) ? periodRaw : DEFAULT_PERIOD;
        const statusRaw = params.get("status") ?? "";
        const status = ["upcoming", "overdue"].includes(statusRaw) ? statusRaw : "";

        this.filters = {
            domain: domainCsv ? domainCsv.split(",").filter(Boolean) : [],
            period,
            status,
            page: Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1),
        };
    }

    reflectFiltersInDom() {
        for (const checkbox of this.domainCheckboxTargets) {
            checkbox.checked = this.filters.domain.includes(checkbox.value);
        }
        for (const button of this.periodBtnTargets) {
            const value = parseInt(button.dataset.period ?? "", 10);
            button.classList.toggle("is-active", value === this.filters.period);
        }
        for (const button of this.statusBtnTargets) {
            const value = button.dataset.status ?? "";
            button.classList.toggle("is-active", value === this.filters.status);
        }
    }

    updateUrl() {
        const params = new URLSearchParams();
        if (this.filters.domain.length) params.set("domain", this.filters.domain.join(","));
        if (this.filters.period !== DEFAULT_PERIOD) params.set("period", String(this.filters.period));
        if (this.filters.status) params.set("status", this.filters.status);
        if (this.filters.page > 1) params.set("page", String(this.filters.page));

        const query = params.toString();
        const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
        window.history.pushState(null, "", url);
    }

    hasActiveFilters() {
        return (
            this.filters.domain.length > 0 ||
            this.filters.period !== DEFAULT_PERIOD ||
            this.filters.status !== "" ||
            this.filters.page > 1
        );
    }

    // -------------------------------------------------------------------------
    // Actions (DOM → état)
    // -------------------------------------------------------------------------

    onDomainChange() {
        this.filters.domain = this.domainCheckboxTargets
            .filter((cb) => cb.checked)
            .map((cb) => cb.value);
        this.filters.page = 1;
        this.updateUrl();
        this.loadObligations();
    }

    onPeriodChange(event) {
        const button = event.currentTarget;
        const period = parseInt(button.dataset.period ?? "", 10);
        if (![30, 90, 180, 365].includes(period)) return;

        this.filters.period = period;
        this.filters.page = 1;
        this.reflectFiltersInDom();
        this.updateUrl();
        this.loadObligations();
    }

    onStatusChange(event) {
        const button = event.currentTarget;
        const status = button.dataset.status ?? "";
        if (status !== "" && !["upcoming", "overdue"].includes(status)) return;

        this.filters.status = status;
        this.filters.page = 1;
        this.reflectFiltersInDom();
        this.updateUrl();
        this.loadObligations();
    }

    resetFilters(event) {
        if (event && typeof event.preventDefault === "function") {
            event.preventDefault();
        }
        this.filters = {
            domain: [],
            period: DEFAULT_PERIOD,
            status: "",
            page: 1,
        };
        this.reflectFiltersInDom();
        this.updateUrl();
        this.loadObligations();
    }

    goToPage(event) {
        const button = event.currentTarget;
        const page = parseInt(button.dataset.page ?? "0", 10);
        if (!Number.isFinite(page) || page < 1) return;

        this.filters.page = page;
        this.updateUrl();
        this.loadObligations();
    }

    // -------------------------------------------------------------------------
    // Appel API + rendu
    // -------------------------------------------------------------------------

    async loadObligations() {
        this.showLoading();
        try {
            const [obligationsRes, personalRes] = await Promise.all([
                this.shouldFetchObligations()
                    ? api.upcomingObligations.list(this.buildRequestParams())
                    : Promise.resolve({ data: [], pagination: null }),
                this.shouldFetchPersonal()
                    ? api.get("/me/personal-reminders?status=active").catch(() => ({ data: [] }))
                    : Promise.resolve({ data: [] }),
            ]);

            const obligationItems = Array.isArray(obligationsRes?.data) ? obligationsRes.data : [];
            const personalItems = Array.isArray(personalRes?.data) ? personalRes.data : [];
            const { dated, undated } = this.splitPersonalReminders(personalItems);

            const merged = mergeSortedByDueDate(obligationItems, dated);
            this.renderUndated(undated);

            const pagination = obligationsRes?.pagination ?? null;

            if (merged.length === 0) {
                this.showEmpty(pagination);

                return;
            }
            this.renderItems(merged);
            this.renderPagination(pagination);
            this.showList();
        } catch {
            // 401 déjà géré globalement par api.js (redirect /connexion.html).
            // Toute autre erreur → état vide neutre (pas de message
            // technique côté UI utilisateur).
            this.renderUndated([]);
            this.showEmpty(null);
        }
    }

    /**
     * Le fetch obligations est court-circuité si l'utilisateur a coché
     * uniquement la catégorie `personal` (filtrage côté client cohérent
     * avec l'UI — la liste obligations système n'est pas chargée si
     * exclue par le filtre).
     */
    shouldFetchObligations() {
        return this.filters.domain.length === 0
            || this.filters.domain.some((d) => d !== PERSONAL_DOMAIN);
    }

    /**
     * Les rappels personnels sont fetched si aucun filtre actif (=tout)
     * ou si `personal` est explicitement coché.
     */
    shouldFetchPersonal() {
        return this.filters.domain.length === 0
            || this.filters.domain.includes(PERSONAL_DOMAIN);
    }

    /**
     * Mappe les `PersonalReminderDto` du backend vers le format
     * `UpcomingObligation` consommé par {@see fillRow()}, en séparant les
     * `nextDueDate IS NULL` (D15 §19.C — section dédiée en bas du
     * calendrier).
     */
    splitPersonalReminders(items) {
        const dated = [];
        const undated = [];
        for (const item of items) {
            if (!item || typeof item !== "object") continue;
            const mapped = {
                domain: PERSONAL_DOMAIN,
                dueDate: item.nextDueDate ?? null,
                label: item.title ?? item.templateKey ?? "",
                periodLabel: "",
                status: null,
                isMarkAsDone: true,
                postResignation: false,
                actionUrl: `/me/personal-reminders/${item.id}/mark-done`,
                _personal: {
                    id: item.id,
                    mandatory: item.mandatory === true,
                },
            };
            if (mapped.dueDate) {
                dated.push(mapped);
            } else {
                undated.push(mapped);
            }
        }

        return { dated, undated };
    }

    renderUndated(items) {
        if (!this.hasUndatedSectionTarget || !this.hasUndatedListTarget) return;
        if (!Array.isArray(items) || items.length === 0) {
            this.undatedSectionTarget.hidden = true;
            this.undatedListTarget.replaceChildren();

            return;
        }
        this.undatedListTarget.replaceChildren();
        for (const item of items) {
            const fragment = this.undatedRowTemplateTarget.content.cloneNode(true);
            const root = fragment.querySelector('[data-slot="root"]');
            const titleEl = fragment.querySelector('[data-slot="title"]');
            const dismissBtn = fragment.querySelector('[data-slot="dismiss-btn"]');
            if (titleEl) titleEl.textContent = item.label;
            if (dismissBtn) {
                if (item._personal?.mandatory) {
                    dismissBtn.remove();
                } else {
                    dismissBtn.addEventListener("click", () => this.dismissPersonal(item._personal.id, root));
                }
            }
            this.undatedListTarget.appendChild(fragment);
        }
        this.undatedSectionTarget.hidden = false;
    }

    buildRequestParams() {
        const params = {
            page: this.filters.page,
            perPage: this.perPageValue,
        };
        if (this.filters.domain.length) {
            params["filter[domain]"] = this.filters.domain.join(",");
        }
        params["filter[period]"] = this.filters.period;
        if (this.filters.status) {
            params["filter[status]"] = this.filters.status;
        }

        return params;
    }

    renderItems(items) {
        this.listTarget.replaceChildren();
        for (const item of items) {
            const fragment = this.rowTemplateTarget.content.cloneNode(true);
            this.fillRow(fragment, item);
            this.listTarget.appendChild(fragment);
        }
    }

    fillRow(fragment, item) {
        const dateRelEl = fragment.querySelector('[data-slot="dateRelative"]');
        const dateAbsEl = fragment.querySelector('[data-slot="dateAbsolute"]');
        const domainPillEl = fragment.querySelector('[data-slot="domainPill"]');
        const labelEl = fragment.querySelector('[data-slot="label"]');
        const periodLabelEl = fragment.querySelector('[data-slot="periodLabel"]');
        const statusBadgeEl = fragment.querySelector('[data-slot="statusBadge"]');
        const postResignationBadgeEl = fragment.querySelector('[data-slot="postResignationBadge"]');
        const linkEl = fragment.querySelector('[data-slot="obligationLink"]');
        const markBtnEl = fragment.querySelector('[data-slot="markAsDoneBtn"]');

        const dueDate = item?.dueDate ?? "";
        if (dateRelEl) {
            dateRelEl.textContent = formatRelativeDate(dueDate);
            if (dueDate) dateRelEl.setAttribute("title", dueDate);
        }
        if (dateAbsEl) {
            dateAbsEl.textContent = formatAbsoluteDateWithWeekday(dueDate);
        }

        if (domainPillEl) {
            const mapping = getDomainPill(item?.domain);
            domainPillEl.className = mapping.class;
            domainPillEl.replaceChildren();
            const dot = document.createElement("span");
            dot.className = "nm-pill__dot";
            domainPillEl.appendChild(dot);
            domainPillEl.append(mapping.label);
        }

        if (labelEl) labelEl.textContent = item?.label ?? "";

        if (periodLabelEl) {
            const periodLabel = item?.periodLabel ?? "";
            if (periodLabel) {
                periodLabelEl.textContent = periodLabel;
            } else {
                periodLabelEl.remove();
            }
        }

        if (statusBadgeEl) {
            this.fillStatusBadge(statusBadgeEl, item?.status);
        }

        if (postResignationBadgeEl) {
            if (item?.postResignation === true) {
                postResignationBadgeEl.hidden = false;
            } else {
                postResignationBadgeEl.remove();
            }
        }

        const isPersonal = item?.domain === PERSONAL_DOMAIN && item?._personal;
        if (linkEl) {
            // Obligation système : lien « Préparer → » vers la page spécialisée
            // (comportement par défaut §15.D l. 119). Le bouton POST « Marquer
            // comme fait » est réservé aux rappels personnels (seul endpoint réel).
            if (!isPersonal && item?.actionUrl) {
                linkEl.setAttribute("href", item.actionUrl);
                linkEl.hidden = false;
            } else {
                linkEl.remove();
            }
        }
        if (markBtnEl) {
            if (isPersonal) {
                markBtnEl.hidden = false;
                markBtnEl.addEventListener("click", (event) => this.markPersonalDone(event, item._personal.id));
            } else {
                markBtnEl.remove();
            }
        }

        // EPIC-066 / 66.2 — saisie du montant payé sur les obligations de
        // charge (URSSAF, TVA, CFE, IR) portant une `periodKey`. Coexiste
        // avec le lien « Préparer → » (geste distinct : montant vs statut).
        const markPaidBtnEl = fragment.querySelector('[data-slot="markPaidBtn"]');
        if (markPaidBtnEl) {
            if (isChargePayable(item)) {
                markPaidBtnEl.hidden = false;
                markPaidBtnEl.setAttribute("aria-expanded", "false");
                markPaidBtnEl.addEventListener("click", (event) => this.toggleObligationPaymentForm(event, item));
            } else {
                markPaidBtnEl.remove();
            }
        }

        const dismissBtnEl = fragment.querySelector('[data-slot="dismissBtn"]');
        if (dismissBtnEl) {
            if (isPersonal && !item._personal.mandatory) {
                dismissBtnEl.hidden = false;
                dismissBtnEl.addEventListener("click", (event) => this.dismissPersonalFromRow(event, item._personal.id));
            } else {
                dismissBtnEl.remove();
            }
        }
    }

    /**
     * Déplie/replie le form inline « Marquer comme payé » sous la ligne
     * de l'obligation (EPIC-066 / 66.2). Le form est inséré comme une
     * `<tr>` pleine largeur juste après la ligne courante.
     */
    toggleObligationPaymentForm(event, item) {
        const button = event.currentTarget;
        const row = button.closest("tr");
        if (!row) return;

        const next = row.nextElementSibling;
        if (next && next.classList.contains("nm-obligation-pay-row")) {
            next.remove();
            button.setAttribute("aria-expanded", "false");

            return;
        }

        const fragment = cloneTemplate("tpl-obligation-payment-form");
        const formRow = fragment.querySelector("tr");
        mountObligationPaymentForm(fragment, {
            obligationKind: item.domain,
            periodKey: item.periodKey,
            onSuccess: () => {
                formRow?.remove();
                button.disabled = true;
                button.textContent = "Payé ✓";
                button.setAttribute("aria-expanded", "false");
            },
            onCancel: () => {
                formRow?.remove();
                button.setAttribute("aria-expanded", "false");
                button.focus();
            },
        });
        row.after(formRow);
        button.setAttribute("aria-expanded", "true");
        formRow?.querySelector('[data-slot="amount"]')?.focus();
    }

    async markPersonalDone(event, reminderId) {
        const button = event.currentTarget;
        if (!button || button.disabled || !reminderId) return;
        button.disabled = true;
        try {
            await api.post(`/me/personal-reminders/${reminderId}/mark-done`, {});
            const tr = button.closest("tr");
            if (tr) tr.remove();
            if (this.listTarget.children.length === 0) {
                this.showEmpty(null);
            }
        } catch {
            button.disabled = false;
        }
    }

    async dismissPersonalFromRow(event, reminderId) {
        const button = event.currentTarget;
        if (!button || button.disabled || !reminderId) return;
        button.disabled = true;
        try {
            await api.post(`/me/personal-reminders/${reminderId}/dismiss`, {});
            const tr = button.closest("tr");
            if (tr) tr.remove();
            if (this.listTarget.children.length === 0) {
                this.showEmpty(null);
            }
        } catch {
            button.disabled = false;
        }
    }

    async dismissPersonal(reminderId, rowEl) {
        if (!reminderId || !rowEl) return;
        const buttons = rowEl.querySelectorAll("button");
        for (const btn of buttons) btn.disabled = true;
        try {
            await api.post(`/me/personal-reminders/${reminderId}/dismiss`, {});
            rowEl.remove();
            if (this.hasUndatedListTarget && this.undatedListTarget.children.length === 0) {
                if (this.hasUndatedSectionTarget) this.undatedSectionTarget.hidden = true;
            }
        } catch {
            for (const btn of buttons) btn.disabled = false;
        }
    }

    fillStatusBadge(badgeEl, status) {
        if (status === "overdue") {
            badgeEl.className = "nm-pill nm-pill--red";
            badgeEl.hidden = false;
            badgeEl.replaceChildren();
            const dot = document.createElement("span");
            dot.className = "nm-pill__dot";
            badgeEl.appendChild(dot);
            badgeEl.append("en retard");
        } else if (status === "not_applicable") {
            badgeEl.className = "nm-pill";
            badgeEl.hidden = false;
            badgeEl.replaceChildren();
            const dot = document.createElement("span");
            dot.className = "nm-pill__dot";
            badgeEl.appendChild(dot);
            badgeEl.append("non applicable");
        } else {
            badgeEl.hidden = true;
        }
    }

    renderPagination(pagination) {
        const captionEl = this.paginationTarget.querySelector("span");
        const pagesEl = this.paginationTarget.querySelector(".nm-pagination__pages");
        if (!captionEl || !pagesEl) return;

        const totalPages = pagination?.totalPages ?? 1;
        const total = pagination?.total ?? 0;
        const page = pagination?.page ?? this.filters.page;
        const perPage = pagination?.perPage ?? this.perPageValue;
        const displayed = Math.max(0, Math.min(perPage, total - (page - 1) * perPage));

        captionEl.textContent = formatPaginationCaption(displayed, total);
        pagesEl.replaceChildren();

        if (totalPages <= 1) return;

        pagesEl.appendChild(this.buildPageButton("«", page > 1 ? page - 1 : null, "Page précédente"));
        for (const entry of getPagesToShow(page, totalPages)) {
            if (entry === "...") {
                pagesEl.appendChild(this.buildPageButton("…", null, "Pages intermédiaires"));
            } else {
                const btn = this.buildPageButton(String(entry), entry, `Page ${entry}`);
                if (entry === page) btn.classList.add("is-current");
                pagesEl.appendChild(btn);
            }
        }
        pagesEl.appendChild(
            this.buildPageButton("»", page < totalPages ? page + 1 : null, "Page suivante"),
        );
    }

    buildPageButton(label, page, title) {
        const fragment = this.pageButtonTemplateTarget.content.cloneNode(true);
        const button = fragment.querySelector("button");
        button.textContent = label;
        button.setAttribute("title", title);
        if (page === null) {
            button.disabled = true;
        } else {
            button.dataset.page = String(page);
            button.dataset.action = "click->upcoming-obligations-page#goToPage";
        }

        return button;
    }

    // -------------------------------------------------------------------------
    // Bascule visibilité (loading / list / empty / emptyAbsolute)
    // -------------------------------------------------------------------------

    showLoading() {
        this.loadingTarget.hidden = false;
        this.resultsTarget.hidden = true;
        this.paginationTarget.hidden = true;
        this.emptyTarget.hidden = true;
        this.emptyAbsoluteTarget.hidden = true;
    }

    showList() {
        this.loadingTarget.hidden = true;
        this.resultsTarget.hidden = false;
        this.paginationTarget.hidden = false;
        this.emptyTarget.hidden = true;
        this.emptyAbsoluteTarget.hidden = true;
    }

    showEmpty(pagination) {
        this.loadingTarget.hidden = true;
        this.resultsTarget.hidden = true;
        this.paginationTarget.hidden = true;
        if (this.hasActiveFilters()) {
            this.emptyTarget.hidden = false;
            this.emptyAbsoluteTarget.hidden = true;
        } else {
            this.emptyTarget.hidden = true;
            this.emptyAbsoluteTarget.hidden = false;
        }
        if (pagination) {
            this.renderPagination(pagination);
        }
    }
}

// -----------------------------------------------------------------------------
// Helpers purs
// -----------------------------------------------------------------------------

function formatPaginationCaption(displayed, total) {
    if (total === 0) return "0 échéance";

    return `${displayed} / ${total} échéance${total > 1 ? "s" : ""}`;
}

function getPagesToShow(current, total) {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages = [1];
    if (current > 3) pages.push("...");
    for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p += 1) {
        pages.push(p);
    }
    if (current < total - 2) pages.push("...");
    pages.push(total);

    return pages;
}

/**
 * Fusionne deux listes triées (par `dueDate` ASC) en une liste unique
 * triée. Les rappels personnels (EPIC-054.5) sont fusionnés avec les
 * obligations système-driven dans la même timeline §15.D ; les items
 * avec `dueDate` falsy sont rejetés en queue (ne devrait pas arriver
 * car les rappels sans date sont écartés en amont par
 * {@see splitPersonalReminders()}).
 */
function mergeSortedByDueDate(a, b) {
    const merged = [...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])];
    merged.sort((x, y) => {
        const xd = x?.dueDate ?? "";
        const yd = y?.dueDate ?? "";
        if (!xd && !yd) return 0;
        if (!xd) return 1;
        if (!yd) return -1;

        return xd < yd ? -1 : (xd > yd ? 1 : 0);
    });

    return merged;
}
